/**
 * DesignersNearYou
 * Real verified-designer profiles for the homeowner dashboard, filtered to the
 * homeowner's city when their profile has one. Replaces the fabricated
 * "AI recommendations" grid (issue #378).
 */

import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SkeletonRows } from "@/components/ui/skeleton";
import type { DesignerProfile } from "@/features/marketplace/services/marketplace-api.service";
import { fetchDesigners } from "@/features/marketplace/services/marketplace-api.service";
import { getHomeownerProfile } from "@/features/settings/services/settings-api.service";
import { StudioButton } from "@/features/studio/theme";
import { DesignerPreviewCard } from "./designer-preview-card";

const MAX_DESIGNERS = 4;

type LoadState =
	| { status: "loading" }
	| { status: "error" }
	| { status: "ready"; designers: DesignerProfile[]; city?: string };

export function DesignersNearYou() {
	const navigate = useNavigate();
	const [state, setState] = useState<LoadState>({ status: "loading" });
	const generationRef = useRef(0);

	const load = useCallback(async () => {
		generationRef.current += 1;
		const generation = generationRef.current;
		setState({ status: "loading" });
		try {
			// A null profile means "no city to filter by", not a failure: the
			// section then shows verified designers without the near-you framing.
			const profile = await getHomeownerProfile();
			const city = profile?.city;
			const result = await fetchDesigners({ city, limit: MAX_DESIGNERS });
			if (generation !== generationRef.current) return;
			setState({
				status: "ready",
				designers: result.designers.slice(0, MAX_DESIGNERS),
				city,
			});
		} catch (err) {
			if (generation !== generationRef.current) return;
			console.error("[DesignersNearYou] Failed to load designers:", err);
			setState({ status: "error" });
		}
	}, []);

	useEffect(() => {
		load();
		return () => {
			generationRef.current += 1;
		};
	}, [load]);

	const city = state.status === "ready" ? state.city : undefined;

	return (
		<div className="dashboard-container pt-20">
			<div className="mb-8 flex items-baseline justify-between">
				<div>
					<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
						{city ? `VERIFIED · NEAR YOU · ${city.toUpperCase()}` : "VERIFIED"}
					</p>
					<h2 className="mt-2 font-display text-[42px] font-medium leading-tight text-dashboard-text tracking-tight">
						{city ? (
							<>
								Designers{" "}
								<span className="italic text-dashboard-accent-display">
									near you.
								</span>
							</>
						) : (
							<>
								Verified{" "}
								<span className="italic text-dashboard-accent-display">
									designers.
								</span>
							</>
						)}
					</h2>
				</div>
				<button
					type="button"
					className="flex h-11 items-center gap-2 rounded-xl border-none bg-none px-5 font-body text-sm font-semibold text-dashboard-muted-dark hover:text-dashboard-text hover:-translate-y-px cursor-pointer"
					onClick={() => navigate("/designers")}
				>
					Browse all
					<ArrowRight className="h-3.75 w-3.57" />
				</button>
			</div>

			{state.status === "loading" && (
				<SkeletonRows
					label="Loading designers..."
					rows={1}
					rowClassName="h-64 rounded-2xl bg-dashboard-border-soft"
				/>
			)}

			{state.status === "error" && (
				<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper px-6 py-10 text-center">
					<p className="font-body text-sm text-dashboard-muted-dark">
						Designers could not load.
					</p>
					<StudioButton
						className="mt-4"
						variant="secondary"
						size="sm"
						style={{
							height: 40,
							paddingLeft: 20,
							paddingRight: 20,
							fontSize: 13,
							fontWeight: 600,
							borderRadius: "var(--radius-xl)",
						}}
						onClick={load}
					>
						Retry
					</StudioButton>
				</div>
			)}

			{state.status === "ready" && state.designers.length === 0 && (
				<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper px-6 py-10 text-center">
					<p className="font-body text-sm text-dashboard-muted-dark">
						{city
							? `No verified designers in ${city} yet.`
							: "No verified designers yet."}
					</p>
					<StudioButton
						className="mt-4"
						variant="secondary"
						size="sm"
						style={{
							height: 40,
							paddingLeft: 20,
							paddingRight: 20,
							fontSize: 13,
							fontWeight: 600,
							borderRadius: "var(--radius-xl)",
						}}
						onClick={() => navigate("/designers")}
					>
						Browse all designers
					</StudioButton>
				</div>
			)}

			{state.status === "ready" && state.designers.length > 0 && (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{state.designers.map((designer) => (
						<DesignerPreviewCard
							key={designer.id}
							profile={designer}
							type="DESIGNER"
							onViewProfile={(id) => navigate(`/designers/${id}`)}
						/>
					))}
				</div>
			)}
		</div>
	);
}
